import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  Directive,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import isNumber from 'lodash/isNumber';

export interface NgbPaginationTextContext {
  totalCount: number;
  pageSize: number;
  pageIndex: number;
  currentPage: number;
}

@Directive({ selector: 'ng-template[ngbPaginationText]' })
export class NgbPaginationText {
  constructor(public templateRef: TemplateRef<NgbPaginationTextContext>) {}
}

export interface NgbPaginationLinkContext {
  /**
   * Page number displayed by the current link.
   */
  currentPage?: number;

  /**
   * If `true`, the current link is disabled.
   */
  disabled: boolean;
}

export interface NgbPaginationNumberContext extends NgbPaginationLinkContext {
  /**
   * The page number, displayed by the current page link.
   */
  $implicit: number;
}

@Directive({ selector: 'ng-template[ngbPaginationNext]' })
export class NgbPaginationNext {
  constructor(public templateRef: TemplateRef<NgbPaginationLinkContext>) {}
}

/**
 * A directive to match the page 'number' link template
 *
 * @since 4.1.0
 */
@Directive({ selector: 'ng-template[ngbPaginationNumber]' })
export class NgbPaginationNumber {
  constructor(public templateRef: TemplateRef<NgbPaginationNumberContext>) {}
}

/**
 * A directive to match the 'previous' link template
 *
 * @since 4.1.0
 */
@Directive({ selector: 'ng-template[ngbPaginationPrevious]' })
export class NgbPaginationPrevious {
  constructor(public templateRef: TemplateRef<NgbPaginationLinkContext>) {}
}

/**
 * A directive to match the 'first' link template
 *
 * @since 4.1.0
 */
@Directive({ selector: 'ng-template[ngbPaginationFirst]' })
export class NgbPaginationFirst {
  constructor(public templateRef: TemplateRef<NgbPaginationLinkContext>) {}
}

/**
 * A directive to match the 'last' link template
 *
 * @since 4.1.0
 */
@Directive({ selector: 'ng-template[ngbPaginationLast]' })
export class NgbPaginationLast {
  constructor(public templateRef: TemplateRef<NgbPaginationLinkContext>) {}
}

@Component({
  selector: 'app-pagination',
  templateUrl: 'pagination.component.html',
  styleUrls: ['pagination.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaginationComponent implements OnChanges {
  @ContentChild(NgbPaginationText, { static: false })
  tplText: NgbPaginationText;

  @ViewChild(NgbPaginationNext, { static: true }) tplNext: NgbPaginationNext;
  @ViewChild(NgbPaginationNumber, { static: true })
  tplNumber: NgbPaginationNumber;
  @ViewChild(NgbPaginationPrevious, { static: true })
  tplPrevious: NgbPaginationPrevious;
  @ViewChild(NgbPaginationFirst, { static: true })
  tplFirst: NgbPaginationFirst;
  @ViewChild(NgbPaginationLast, { static: true }) tplLast: NgbPaginationLast;

  @Input()
  disabled: boolean;
  @Input()
  totalCount: number;
  @Input()
  pageSize: number;
  @Input()
  pageIndex: number;
  @Output()
  pageIndexChange = new EventEmitter<number>();

  currentPage: number;
  page: number = 1;

  pageCount = 0;
  pages: number[] = [];

  maxSize: number = 2;
  ellipses: boolean = true;
  rotate: boolean = true;
  boundaryLinks: boolean = false;
  directionLinks: boolean = true;
  size = 'sm';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.pageIndex) {
      this.page = this.currentPage = Math.max((this.pageIndex ?? 0) + 1, 1);
    }

    this._updatePages(this.page);
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.pageIndex = page - 1;
    this.pageIndexChange.emit(this.pageIndex);
  }

  hasPrevious(): boolean {
    return this.page > 1;
  }

  hasNext(): boolean {
    return this.page < this.pageCount;
  }

  nextDisabled(): boolean {
    return !this.hasNext() || this.disabled;
  }

  previousDisabled(): boolean {
    return !this.hasPrevious() || this.disabled;
  }

  selectPage(pageNumber: number): void {
    pageNumber = getValueInRange(pageNumber, this.pageCount, 1);
    this._updatePages(pageNumber);
  }

  isEllipsis(pageNumber): boolean {
    return pageNumber === -1;
  }

  private _applyEllipses(start: number, end: number) {
    if (this.ellipses) {
      if (start > 0) {
        // The first page will always be included. If the displayed range
        // starts after the third page, then add ellipsis. But if the range
        // starts on the third page, then add the second page instead of
        // an ellipsis, because the ellipsis would only hide a single page.
        if (start >= 2) {
          this.pages.unshift(-1);
        }

        this.pages.unshift(1);
      }
      if (end < this.pageCount) {
        // The last page will always be included. If the displayed range
        // ends before the third-last page, then add ellipsis. But if the range
        // ends on third-last page, then add the second-last page instead of
        // an ellipsis, because the ellipsis would only hide a single page.
        if (end <= this.pageCount - 2) {
          this.pages.push(-1);
        }

        this.pages.push(this.pageCount);
      }
    }
  }

  /**
   * Rotates page numbers based on maxSize items visible.
   * Currently selected page stays in the middle:
   *
   * Ex. for selected page = 6:
   * [5,*6*,7] for maxSize = 3
   * [4,5,*6*,7] for maxSize = 4
   */
  private _applyRotation(): [number, number] {
    let start = 0;
    let end = this.pageCount;
    let leftOffset = Math.floor(this.maxSize / 2);
    let rightOffset = leftOffset;

    if (this.page <= leftOffset) {
      // very beginning, no rotation -> [0..maxSize]
      end = this.maxSize;
    } else if (this.pageCount - this.page < leftOffset) {
      // very end, no rotation -> [len-maxSize..len]
      start = this.pageCount - this.maxSize;
    } else {
      // rotate
      start = this.page - leftOffset - 1;
      end = this.page + rightOffset;
    }

    return [start, end];
  }

  /**
   * Paginates page numbers based on maxSize items per page.
   */
  private _applyPagination(): [number, number] {
    let page = Math.ceil(this.page / this.maxSize) - 1;
    let start = page * this.maxSize;
    let end = start + this.maxSize;

    return [start, end];
  }

  private _setPage(newPageNo) {
    const prevPageNo = this.page;
    this.page = newPageNo;

    if (this.page !== prevPageNo && isNumber(this.totalCount)) {
      this.pageIndexChange.emit(this.page - 1);
    }
  }

  private _updatePages(newPage: number) {
    this.pageCount =
      this.pageSize < 1 ? 0 : Math.ceil(this.totalCount / this.pageSize);

    if (!isNumber(this.pageCount)) {
      this.pageCount = 0;
    }

    // fill-in model needed to render pages
    this.pages.length = 0;
    for (let i = 1; i <= this.pageCount; i++) {
      this.pages.push(i);
    }

    // set page within 1..max range
    this._setPage(newPage);

    // apply maxSize if necessary
    if (this.maxSize > 0 && this.pageCount > this.maxSize) {
      let start = 0;
      let end = this.pageCount;

      // either paginating or rotating page numbers
      if (this.rotate) {
        [start, end] = this._applyRotation();
      } else {
        [start, end] = this._applyPagination();
      }

      this.pages = this.pages.slice(start, end);

      // adding ellipses
      this._applyEllipses(start, end);
    }
  }
}

function getValueInRange(value: number, max: number, min = 0): number {
  return Math.max(Math.min(value, max), min);
}
